<script setup lang="ts">
import type { UserAvatarEntity, UserAvatarInitialsUi } from '#layers/access/types/user-avatar';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getStableColorFromString } from '#layers/ui';

const { user, ui } = defineProps<{
	user: UserAvatarEntity;
	ui?: UserAvatarInitialsUi;
}>();

const { t } = useI18n();

const initials = computed(() => getInitials(getUserOrServiceAccountDisplayName(user)));
const backgroundColor = computed(() => ui?.content ? undefined : getStableColorFromString(user.id));

// Baseline sits 2% below the viewBox center to optically balance the top-heavy, descender-less capitals.
const baselineY = 52;

function getInitials(name: string): string {
	return name
		.split(' ')
		.map((word) => word.charAt(0))
		.join('')
		.slice(0, 2);
}

function getUserOrServiceAccountDisplayName(entity: UserAvatarEntity): string {
	if ('email' in entity) {
		return entity.fullName ?? entity.email ?? t('common.unknown_user');
	}

	return entity.fullName ?? t('common.unknown_service_account');
}
</script>

<template>
	<div
		class="size-full"
		:class="ui?.content ?? [ui?.root ?? '', ui?.fallback ?? 'text-white']"
		:style="backgroundColor ? { backgroundColor } : undefined"
	>
		<svg class="size-full" viewBox="0 0 100 100" role="img" :aria-label="initials">
			<text
				x="50"
				:y="baselineY"
				text-anchor="middle"
				dominant-baseline="central"
				fill="currentColor"
				font-size="40"
				font-weight="500"
			>{{ initials }}</text>
		</svg>
	</div>
</template>
